// SCRUM-1961 (epic SCRUM-1954) — "new version available" banner.
//
// Replaces the old auto `location.reload()` that interrupted readers whenever a
// new frontend build was deployed. VersionCheckService now only *flags* a new
// build; this banner surfaces it as a calm, dismissible prompt pinned to the
// bottom of the shell. The page is never reloaded until the user taps Refresh,
// so nothing moves under them while they read.
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VersionCheckService } from '../services/version-check.service';

@Component({
  selector: 'app-update-available-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
    <div class="uab" *ngIf="(version.updateAvailable$ | async) && !dismissed"
         role="status" data-testid="update-available-banner">
      <span class="uab-msg">A new version is available.</span>
      <div class="uab-actions">
        <button type="button" class="uab-refresh" data-testid="update-refresh"
                (click)="refresh()">Refresh</button>
        <button type="button" class="uab-dismiss" aria-label="Dismiss"
                data-testid="update-dismiss" (click)="dismiss()">Later</button>
      </div>
    </div>
  `,
  styles: [`
    .uab {
      position: fixed; left: 50%; bottom: var(--space-4, 1rem);
      transform: translateX(-50%); z-index: 1200;
      display: flex; align-items: center; gap: var(--space-4, 1rem);
      max-width: calc(100vw - 2rem);
      padding: var(--space-3, 0.75rem) var(--space-4, 1rem);
      background: var(--ag-surface, #fff); color: var(--ag-text, #111827);
      border: 1px solid var(--ag-border, #e5e7eb);
      border-radius: var(--radius-md, 8px); box-shadow: var(--elev-3, 0 8px 24px rgba(0,0,0,0.18));
      font-size: var(--fs-body-sm, 0.8125rem);
    }
    .uab-msg { white-space: nowrap; }
    .uab-actions { display: flex; align-items: center; gap: var(--space-2, 0.5rem); }
    .uab-refresh {
      border: none; cursor: pointer; font-weight: var(--fw-semibold, 600);
      padding: var(--space-1, 0.25rem) var(--space-3, 0.75rem);
      border-radius: var(--radius-sm, 4px);
      background: var(--ag-primary, #0B6A43); color: #fff;
    }
    .uab-dismiss {
      border: none; cursor: pointer; background: transparent;
      color: var(--ag-text-secondary, #6b7280);
      padding: var(--space-1, 0.25rem) var(--space-2, 0.5rem);
    }
    @media (max-width: 767px) {
      .uab { left: var(--space-3, 0.75rem); right: var(--space-3, 0.75rem);
             transform: none; max-width: none; }
      .uab-msg { white-space: normal; }
    }
  `],
})
export class UpdateAvailableBannerComponent {
  readonly version = inject(VersionCheckService);
  dismissed = false;

  refresh(): void {
    this.version.applyUpdate();
  }
  dismiss(): void {
    // Hide for this reading session; the flag stays set so the new build still
    // applies on the user's next full navigation/refresh.
    this.dismissed = true;
  }
}
